import {baseApi} from 'core/redux/store/api';
import {EndpointDefinition, FetchArgs} from '@reduxjs/toolkit/query';
import {TGetListingTransformedResponse, TGetListingResponse} from 'plugins/modules/listing/types';

export type TGetListingRequest<T = {}> = {
    filter: string;
    pageNumber: number;
    pageSize: number;
    sort?: string;
    withMeta?: boolean;
    withSeo?: boolean;
    expressOrderStoreId?: string;
    pagination: {
        isManual: boolean;
        firstPage: number;
        lastPage: number;
    };
} & T;

const REFETCH_VALUES_DEFAULT_KEYS = [
    'filter',
    'pageNumber',
    'pageSize',
    'section',
    'withMeta',
    'withSeo',
    'sort',
    'expressOrderStoreId',
];

interface ICreateListingApi<Product, Response, ResponseExtended, RequestExtended> {
    name: string;
    query: (query: TGetListingRequest<RequestExtended>) => FetchArgs;
    forceRefetchValuesKeys: string[];
    transformResponse?: (r: Response) => TGetListingTransformedResponse<Product, ResponseExtended>;
    serializeQueryArgs?: (p: {
        endpointName: string;
        queryArgs: RequestExtended;
        endpointDefinition: EndpointDefinition<RequestExtended, any, any, any>;
    }) => string;
}

export function createListingApiService<
    Product,
    Response extends TGetListingResponse<Product>,
    TransformedResponse extends TGetListingTransformedResponse<Product> = TGetListingTransformedResponse<Product>,
    RequestExtended = {},
>({
    name,
    query,
    forceRefetchValuesKeys = [],
    transformResponse,
    serializeQueryArgs,
}: ICreateListingApi<Product, Response, TransformedResponse, RequestExtended>) {
    return baseApi.injectEndpoints({
        overrideExisting: true,
        endpoints: (builder) => ({
            [name]: builder.query({
                query,
                providesTags: ['ProductList'],
                transformResponse: transformResponse
                    ? transformResponse
                    : (response: Response): TGetListingTransformedResponse<Product> => ({
                          list: response?.data?.items,
                          pagination: response?.pagination,
                          meta: response?.meta ?? null,
                          seo: response?.seo ?? null,
                      }),
                // eslint-disable-next-line complexity
                merge: (currentCache, newData, {arg}) => {
                    const pageNumber = arg.pageNumber || 0;
                    const prevPageNumber = currentCache.pagination?.pageNumber || 0;
                    const direction = pageNumber >= prevPageNumber ? 'next' : 'prev';
                    const isPageChanged = currentCache?.pagination?.pageNumber !== newData?.pagination?.pageNumber;

                    if (!newData || !newData.list) {
                        return;
                    }

                    if (isPageChanged && !arg.pagination.isManual) {
                        currentCache.list =
                            'next' === direction
                                ? [...currentCache.list, ...newData.list]
                                : [...newData.list, ...currentCache.list];
                    } else {
                        currentCache.list = newData.list;
                    }
                    currentCache.pagination = newData.pagination;
                    currentCache.meta = newData.meta;
                    currentCache.seo = newData.seo ?? currentCache.seo;
                },
                serializeQueryArgs: ({endpointName, queryArgs, endpointDefinition}) => {
                    if (serializeQueryArgs) {
                        return serializeQueryArgs({endpointName, queryArgs, endpointDefinition});
                    }

                    return `${endpointName}`;
                },
                forceRefetch: ({currentArg, previousArg}) => {
                    const keys = [...REFETCH_VALUES_DEFAULT_KEYS, ...forceRefetchValuesKeys];

                    return keys.some(
                        (key) =>
                            currentArg?.[key as keyof TGetListingRequest] !==
                            previousArg?.[key as keyof TGetListingRequest],
                    );
                },
            }),
        }),
    });
}
